import { NavigationAction, useNavigation } from '@react-navigation/native';
import {
  StackNavigationOptions,
  StackNavigationProp,
} from '@react-navigation/stack';
import { AnimatePresence, MotiView } from 'moti';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { KeyboardAvoidingView, TouchableWithoutFeedback } from 'react-native';
import { Easing } from 'react-native-reanimated';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { CentralizedScrollView } from '../../CentralizedScrollView';

const marginHorizontal = 5;
const marginVertical = 30;

export interface FlexModalWrapperRef {
  dismiss(): void;
}

export interface FlexModalWrapperProps {
  dismissible?: boolean;
  maxWidth?: number;
  minHeight?: number;
  children?: React.ReactNode;
  scrollEnabled?: boolean;
}

/**
 * ModalWrapper that can contains long content
 */
export const FlexModalWrapper = forwardRef<
  FlexModalWrapperRef,
  FlexModalWrapperProps
>((props, ref) => {
  const [visible, setVisible] = useState(true);
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { top } = useSafeAreaInsets();
  const continueAction = useRef<NavigationAction>();

  useImperativeHandle(
    ref,
    () => ({
      dismiss: () => {
        setVisible(false);
      },
    }),
    [],
  );

  useEffect(() => {
    if (!visible) return;

    return navigation.addListener('beforeRemove', e => {
      e.preventDefault();
      continueAction.current = e.data.action;
      setVisible(false);
    });
  }, [navigation, visible]);

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (continueAction.current != null) {
          navigation.dispatch(continueAction.current);
        } else {
          navigation.goBack();
        }
      }}
    >
      {visible && (
        <MotiView
          from={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: 'timing', duration: 300 }}
          exitTransition={{
            type: 'spring',
            overshootClamping: true,
          }}
          css={`
            flex: 1;
            padding-top: ${top}px;
            background-color: rgba(0, 0, 0, 0.54);
          `}
        >
          <TouchableWithoutFeedback
            onPress={() => props.dismissible && setVisible(false)}
          >
            <KeyboardAvoidingView
              behavior={'padding'}
              css={`
                flex: 1;
                justify-content: center;
              `}
            >
              <CentralizedScrollView
                scrollEnabled={props.scrollEnabled}
                contentContainerStyle={[
                  { width: '95%', marginLeft: 'auto', marginRight: 'auto' },
                  props.maxWidth != null && { maxWidth: props.maxWidth },
                ]}
              >
                <TouchableWithoutFeedback>
                  <MotiView
                    css={`
                      margin: ${marginVertical}px ${marginHorizontal}px;
                      background-color: white;
                      border-radius: 15px;
                      overflow: hidden;
                      align-self: center;
                      width: 100%;
                      min-height: ${props.minHeight ?? 600}px;
                    `}
                    from={{ translateY: 400 }}
                    animate={{ translateY: 0 }}
                    transition={{
                      type: 'timing',
                      duration: 250,
                      easing: Easing.in(Easing.linear),
                    }}
                    exit={{ translateY: 400 }}
                    exitTransition={{
                      type: 'spring',
                      overshootClamping: true,
                    }}
                  >
                    <SafeAreaInsetsContext.Provider
                      value={{ top: 0, left: 0, right: 0, bottom: 0 }}
                    >
                      {props.children}
                    </SafeAreaInsetsContext.Provider>
                  </MotiView>
                </TouchableWithoutFeedback>
              </CentralizedScrollView>
            </KeyboardAvoidingView>
          </TouchableWithoutFeedback>
        </MotiView>
      )}
    </AnimatePresence>
  );
});

export const modalWrapperOptions = (): StackNavigationOptions => ({
  presentation: 'transparentModal',
  cardOverlayEnabled: true,
  animationEnabled: false,
  detachPreviousScreen: false,
  cardStyle: {
    backgroundColor: 'transparent',
  },
  headerShown: false,
});
